// A referral is two people: whoever picked up, and whoever they sent you to.
// Pure shaping of that pair — the note the call is logged with, the contact
// row the referred person becomes, and the key that promotes them to primary.

export interface ReferralDraft {
  // Who actually answered. Often the listed contact, often a receptionist.
  spokeWith: string
  spokeWithRole: string
  // Who they sent you to.
  name: string
  role: string
  phone: string
  email: string
}

export const emptyReferral = (spokeWith = ''): ReferralDraft => ({
  spokeWith,
  spokeWithRole: '',
  name: '',
  role: '',
  phone: '',
  email: '',
})

export interface ReferralContactFields {
  name: string
  role?: string
  phone?: string
  email?: string
}

const t = (v: string): string => v.trim()

/** A referral is only worth recording once the referred person has a name. */
export const isReferralComplete = (r: ReferralDraft): boolean => !!t(r.name)

/**
 * The contact row to append to the lead. Empty fields are dropped rather than
 * sent as '' — a blank override would mask a value the enrichment already has.
 */
export function referralContactFields(r: ReferralDraft): ReferralContactFields {
  const fields: ReferralContactFields = { name: t(r.name) }
  if (t(r.role)) fields.role = t(r.role)
  if (t(r.phone)) fields.phone = t(r.phone)
  if (t(r.email)) fields.email = t(r.email)
  return fields
}

/**
 * Key to promote the referred person by. Email when they gave one, lowercased
 * name otherwise — the same email-or-name key the primary override resolves
 * against on both sides, so it survives the contact being re-enriched.
 */
export const referralPrimaryKey = (r: ReferralDraft): string =>
  t(r.email).toLowerCase() || t(r.name).toLowerCase()

const withRole = (name: string, role: string): string =>
  t(role) ? `${t(name)} (${t(role)})` : t(name)

/**
 * The call's note. Names both halves in one line so the next BDR reads who was
 * spoken to and who that got them, without opening the contact list.
 */
export function composeReferralNote(r: ReferralDraft): string {
  const to = withRole(r.name, r.role)
  const phone = t(r.phone)
  const target = phone ? `${to} · ${phone}` : to
  return t(r.spokeWith)
    ? `Pratade med ${withRole(r.spokeWith, r.spokeWithRole)} — hänvisad till ${target}`
    : `Hänvisad till ${target}`
}
